import { addInDefaultTimeZone } from 'DateHelpers';
import {
    type GradingFields,
    type IdVerificationGate,
    type ProjectFieldsModalProject,
    type ResubmissionContext,
    type ScoreOptionValue,
} from './ProjectFieldsModal.types';

// Pure ports of two things: the computed getters and setters on the Iguana ProjectProgress model,
// and the resubmission and approval logic from the legacy project_fields_modal directive. The model
// lives at LearnerProjects/angularModule/scripts/models/project_progress.js.
//
// Pure functions let the React form own the grading state without an Angular model. They also let us
// test the behavior against the original Angular spec, one case at a time.

// 15 days out. A student sees 14, because we show the deadline as the day before.
const DEFAULT_RESUBMISSION_WINDOW_DAYS = 15;

export const hasScoreSet = (fields: Pick<GradingFields, 'score'>): boolean =>
    fields.score !== null && fields.score !== undefined;

export const isProvisional = (fields: Pick<GradingFields, 'score' | 'waived'>): boolean =>
    !hasScoreSet(fields) && !fields.waived;

// Whether the form grades provisionally. The provisional grade lives on the submission (#17209), so
// with no submission the form edits the real score and feedback. The helpers below keep the plain
// isProvisional: with no submission the provisional score is always null, so they already return what
// the direct path needs.
export const gradesProvisionally = (fields: Pick<GradingFields, 'score' | 'waived'>, hasSubmission: boolean): boolean =>
    hasSubmission && isProvisional(fields);

export const hasProvisionalScoreSet = (fields: Pick<GradingFields, 'score' | 'waived' | 'provisionalScore'>): boolean =>
    isProvisional(fields) && fields.provisionalScore !== null && fields.provisionalScore !== undefined;

export function scoreOptionValue(
    fields: Pick<GradingFields, 'resubmissionRequired' | 'waived' | 'score'>,
): ScoreOptionValue {
    if (fields.resubmissionRequired) return 'resubmission_required';
    if (fields.waived) return 'waived';
    if (fields.score === null || fields.score === undefined) return null;
    return fields.score;
}

export function provisionalScoreOptionValue(
    fields: Pick<GradingFields, 'resubmissionRequired' | 'waived' | 'score' | 'provisionalScore'>,
): ScoreOptionValue {
    // Call scoreOptionValue first, so a waived or approved score wins.
    const approvedValue = scoreOptionValue(fields);
    if (approvedValue !== null) return approvedValue;
    // A provisional resubmission_required leaves the score null, so handle it here.
    if (fields.resubmissionRequired) return 'resubmission_required';
    if (fields.provisionalScore === null || fields.provisionalScore === undefined) return null;
    return fields.provisionalScore;
}

export function isScoreFailing(
    fields: Pick<GradingFields, 'score' | 'waived' | 'provisionalScore'>,
    passingScore: number,
): boolean {
    const currentScore = isProvisional(fields) ? fields.provisionalScore : fields.score;
    if (currentScore === null || currentScore === undefined || typeof currentScore !== 'number') return false;
    return currentScore < passingScore;
}

// A new score clears any provisional score. A numeric score also defaults the status to 'submitted'.
export function setScoreOptionValue(fields: GradingFields, val: ScoreOptionValue): GradingFields {
    const next: GradingFields = { ...fields, provisionalScore: null };

    if (val === 'resubmission_required') {
        next.resubmissionRequired = true;
        next.waived = false;
        next.score = 0;
        next.allowResubmission = true;
        // Unlike waived, resubmission_required leaves the status alone.
    } else if (val === 'waived') {
        next.resubmissionRequired = false;
        next.allowResubmission = false;
        next.waived = true;
        next.score = null;
        next.status = null;
    } else if (val === null || val === undefined) {
        next.resubmissionRequired = false;
        next.waived = false;
        next.score = null;
    } else {
        next.resubmissionRequired = false;
        next.waived = false;
        next.score = Number(val);
        next.status = next.status || 'submitted';
    }

    return next;
}

// Every branch clears `waived`. If it stayed true, a numeric or blank pick after Waived would snap
// the select back to Waived, and a save would send `waived: true` with a provisional score.
export function setProvisionalScoreOptionValue(fields: GradingFields, val: ScoreOptionValue): GradingFields {
    // Waived leaves the provisional state, so reuse the score setter.
    if (val === 'waived') return setScoreOptionValue(fields, 'waived');

    const next: GradingFields = { ...fields };

    if (val === 'resubmission_required') {
        // Stay provisional, so the approval action stays available.
        next.resubmissionRequired = true;
        next.waived = false;
        next.provisionalScore = 0;
        next.score = null;
        next.status = next.status || 'submitted';
        return next;
    }

    if (val === null || val === undefined) {
        next.resubmissionRequired = false;
        next.waived = false;
        next.provisionalScore = null;
        return next;
    }

    next.resubmissionRequired = false;
    next.waived = false;
    next.provisionalScore = Number(val);
    next.status = next.status || 'submitted';
    return next;
}

// Ports `recalculateAllowResubmission` from the $watchGroup body in the legacy directive. It returns
// the fields with a new `allowResubmission`. The branch order matters and must match the original
// exactly. When nothing changed it returns the fields untouched, which keeps a saved value.
export function recalculateAllowResubmission(fields: GradingFields, context: ResubmissionContext): GradingFields {
    const { passingScore, approve, similarityCheckRequired, historicalSubmissionsCount, hasChanges } = context;

    if (!hasChanges) return fields;

    const provisional = isProvisional(fields);
    const withAllow = (allowResubmission: boolean): GradingFields => ({ ...fields, allowResubmission });

    // resubmission_required forces a resubmission, but only once it takes effect. It takes effect
    // when the grader approves, or when the record is not provisional.
    if (fields.resubmissionRequired && (!provisional || approve)) return withAllow(true);

    if (!isScoreFailing(fields, passingScore)) return withAllow(false);

    if (fields.status === 'plagiarized') return withAllow(false);

    if (
        similarityCheckRequired &&
        fields.projectSubmissionSimilarityAndOriginalityStatus === 'issues_detected_no_resubmission_allowed'
    ) {
        return withAllow(false);
    }

    if (historicalSubmissionsCount >= 3) return withAllow(false);

    if (provisional && !approve) return withAllow(false);

    return withAllow(true);
}

// An active resubmission_required approves on its own, as the directive's pre-recalc step did.
export function shouldForceApproval(fields: Pick<GradingFields, 'resubmissionRequired' | 'score' | 'waived'>): boolean {
    return !!fields.resubmissionRequired && isProvisional(fields);
}

// Ports the due-date part of the `allow_resubmission` watcher. When resubmission turns on, set a
// default deadline, or restore a saved deadline that is still in the future. When it turns off,
// clear the deadline.
export function applyAllowResubmissionDueDate(
    fields: GradingFields,
    originalDueDate: number | null,
    nowSeconds: number,
): GradingFields {
    if (!fields.allowResubmission) {
        return { ...fields, resubmissionDueDate: null };
    }

    // Keep an existing deadline, even a past one. Do not move it without a cue.
    if (fields.resubmissionDueDate !== null && fields.resubmissionDueDate !== undefined) {
        return fields;
    }

    // Restore the saved deadline when it is still in the future.
    if (originalDueDate !== null && originalDueDate !== undefined && originalDueDate >= nowSeconds) {
        return { ...fields, resubmissionDueDate: originalDueDate };
    }

    return {
        ...fields,
        resubmissionDueDate: defaultResubmissionDueDate(nowSeconds),
    };
}

// Gates Save-and-Approve on the ID Verified field, for a presentation project (#17035). `approve`
// marks the Save-and-Approve action. In Angular that was the "Approve score and feedback when I
// save?" checkbox. IdVerificationGate documents each return value.
export function idVerificationGate(
    fields: Pick<GradingFields, 'idVerified' | 'score' | 'waived' | 'provisionalScore'>,
    project: Pick<ProjectFieldsModalProject, 'isPresentationProject' | 'passingScore'>,
    approve: boolean,
): IdVerificationGate {
    if (!project.isPresentationProject) return 'na';
    if (!approve) return 'na';
    // A failing score can never count as passing, so its score and feedback can reach the student
    // without ID verification. This includes a resubmission handback, which always scores 0 (#17169).
    // See https://trello.com/c/ma1GutqW.
    if (isScoreFailing(fields, project.passingScore)) return 'na';
    if (fields.idVerified === null || fields.idVerified === undefined) return 'blank';
    if (fields.idVerified === false) return 'auto_flag';
    return 'ok';
}

// Save-and-Approve promotes the provisional grade into the real columns, so it needs a provisional
// score. Blanking an approved score also makes the record provisional again. Without the score check
// the green button stayed enabled and the save wrote a null score over the saved feedback.
export function isApproveDisabled(fields: GradingFields, gate: IdVerificationGate, isSaving: boolean): boolean {
    return !hasProvisionalScoreSet(fields) || isSaving || gate === 'blank';
}

// The auto-flag transform for a save. It applies when the grader approves a presentation project
// whose ID is not verified (#17035). It routes to admin review instead of approving. The provisional
// score and feedback stay as they are. It clears the resubmission fields, so the server's
// validate_resubmission_required_fields passes. An admin can set them again after verifying the ID.
export function applyAutoFlagForAdminReview(fields: GradingFields): GradingFields {
    return {
        ...fields,
        projectSubmissionNeedsAdminReview: true,
        resubmissionRequired: false,
        allowResubmission: false,
    };
}

// The approval transform for a save. It copies the provisional score and feedback into the real
// columns, then clears the provisional fields. It does nothing unless the record is provisional,
// which matches `saveChanges`.
export function applyApproval(fields: GradingFields): GradingFields {
    if (!isProvisional(fields)) return fields;
    return {
        ...fields,
        score: fields.provisionalScore,
        feedback: fields.provisionalFeedback,
        provisionalScore: null,
        provisionalFeedback: null,
    };
}

// Mirrors the original directive exactly. Add the window in the app's default timezone,
// America/New_York, so the day count stays correct across a DST change. Then truncate to the start
// of the day in the viewer's local timezone. It returns a unix-seconds timestamp.
function defaultResubmissionDueDate(nowSeconds: number): number {
    const date = addInDefaultTimeZone(new Date(nowSeconds * 1000), DEFAULT_RESUBMISSION_WINDOW_DAYS, 'days');
    date.setHours(0, 0, 0, 0);
    return Math.floor(date.getTime() / 1000);
}
